/**
 * Retainer data and derived state for matter detail from `retainersForMatter` (Apollo).
 * Composed by useMatterDetail.
 *
 * TODO(SYN-72): When Payment ships, derive balance / funded state from `paidAt`, purchases, and
 * hours consumed; align UI with backend billing. Until then, balance fields are best-effort from
 * approved retainers (e.g. `hoursRequested`, `flatFee`) only — not a source of payment truth.
 */
import { useMemo } from 'react';
import { getHiredExpertIds } from '~/modules/matters/matterExpertUtils';
import {
  RetainerFeeType,
  RetainerStatus,
  type RetainerFieldsFragment,
} from '~/gql/graphql';
import type { MatterViewModel } from '~/modules/matters/matters.types';
import { CENTS_PER_DOLLAR } from '~/shared/constants';
import type { User } from '~/shared/types/connect';
import { useRetainersForMatter } from '~/modules/matters/useRetainersForMatter';
import type { RetainerStatusFilterValue } from '~/modules/matters/useMatterDetailModals';

export interface UseMatterDetailRetainersParams {
  matterId: string | undefined;
  matter: MatterViewModel | null;
  retainerStatusFilter: RetainerStatusFilterValue;
  retainerExpertFilter: string;
  getExpertById: (id: string) => User | undefined;
}

export interface UseMatterDetailRetainersReturn {
  retainers: RetainerFieldsFragment[];
  isLoadingRetainers: boolean;
  retainersError: ReturnType<typeof useRetainersForMatter>['error'];
  balance: {
    hoursPurchased: number;
    flatAmountPurchased?: number;
    hoursUsed?: number;
    hoursRemaining?: number;
  } | null;
  /** Approved hourly retainers (work may be complete or not). */
  activeHourlyRetainers: RetainerFieldsFragment[];
  /** Approved flat-fee retainers. */
  activeFlatRetainers: RetainerFieldsFragment[];
  pendingRequests: RetainerFieldsFragment[];
  retainerExpertsForFilter: Array<{ id: string; name: string }>;
  filteredPendingRequests: RetainerFieldsFragment[];
  filteredActiveFlatRetainers: RetainerFieldsFragment[];
  filteredActiveHourlyRetainers: RetainerFieldsFragment[];
  noRetainerBalance: boolean;
  hasHoursBalance: boolean;
  hasFlatBalance: boolean;
  pendingApproveLabel: string;
  /** Closed or declined retainers for history UI (derived from `retainersForMatter`, sorted by `updatedAt`). */
  historyRetainers: RetainerFieldsFragment[];
}

function isApproved(retainer: RetainerFieldsFragment): boolean {
  return retainer.status === RetainerStatus.Approved;
}

function isPending(retainer: RetainerFieldsFragment): boolean {
  return retainer.status === RetainerStatus.Pending;
}

function isClosedOrDeclined(retainer: RetainerFieldsFragment): boolean {
  return (
    retainer.status === RetainerStatus.Closed ||
    retainer.status === RetainerStatus.Declined
  );
}

function getExpertId(retainer: RetainerFieldsFragment): string {
  return retainer.matterExpert.expertId;
}

function hoursRequestedNumber(retainer: RetainerFieldsFragment): number {
  const raw = retainer.hoursRequested;
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function useMatterDetailRetainers({
  matterId,
  matter,
  retainerStatusFilter,
  retainerExpertFilter,
  getExpertById,
}: UseMatterDetailRetainersParams): UseMatterDetailRetainersReturn {
  const {
    retainers: retainersRaw,
    isLoading,
    error,
  } = useRetainersForMatter(matterId);

  const retainers = useMemo(() => retainersRaw ?? [], [retainersRaw]);

  const balance = useMemo(() => {
    if (!matterId) {
      return null;
    }
    const approved = retainers.filter(isApproved);
    let hoursPurchased = 0;
    let flatDollars = 0;
    for (const retainer of approved) {
      if (retainer.feeType === RetainerFeeType.Hourly) {
        hoursPurchased += hoursRequestedNumber(retainer);
      } else if (retainer.feeType === RetainerFeeType.FlatRate) {
        flatDollars += retainer.subtotal / CENTS_PER_DOLLAR;
      }
    }
    return {
      hoursPurchased,
      flatAmountPurchased: flatDollars > 0 ? flatDollars : undefined,
      hoursUsed: undefined,
      hoursRemaining: undefined,
    };
  }, [matterId, retainers]);

  const pendingRequests = useMemo(
    () => retainers.filter(isPending),
    [retainers],
  );

  const approvedRetainers = useMemo(
    () => retainers.filter(isApproved),
    [retainers],
  );

  const activeHourlyRetainers = useMemo(
    () =>
      approvedRetainers.filter(
        (retainer) => retainer.feeType === RetainerFeeType.Hourly,
      ),
    [approvedRetainers],
  );

  const activeFlatRetainers = useMemo(
    () =>
      approvedRetainers.filter(
        (retainer) => retainer.feeType === RetainerFeeType.FlatRate,
      ),
    [approvedRetainers],
  );

  const historyRetainers = useMemo(
    () =>
      [...retainers].filter(isClosedOrDeclined).sort((retainerA, retainerB) => {
        const timeA = new Date(retainerB.updatedAt).getTime();
        const timeB = new Date(retainerA.updatedAt).getTime();
        return timeA - timeB;
      }),
    [retainers],
  );

  const retainerExpertsForFilter = useMemo(() => {
    const expertIds = new Set<string>();
    pendingRequests.forEach((request) => expertIds.add(getExpertId(request)));
    activeHourlyRetainers.forEach((retainer) =>
      expertIds.add(getExpertId(retainer)),
    );
    activeFlatRetainers.forEach((retainer) =>
      expertIds.add(getExpertId(retainer)),
    );
    getHiredExpertIds(matter?.matterExperts).forEach((expertId) =>
      expertIds.add(expertId),
    );
    return Array.from(expertIds)
      .map((id) => {
        const user = getExpertById(id);
        return user ? { id: user.id, name: user.name } : null;
      })
      .filter((entry): entry is { id: string; name: string } => entry !== null);
  }, [
    pendingRequests,
    activeHourlyRetainers,
    activeFlatRetainers,
    matter?.matterExperts,
    getExpertById,
  ]);

  const filterByExpert = useMemo(() => {
    if (retainerExpertFilter === 'all' || !retainerExpertFilter) {
      return () => true;
    }
    return (expertId: string) => expertId === retainerExpertFilter;
  }, [retainerExpertFilter]);

  const filteredPendingRequests = useMemo(
    () =>
      pendingRequests.filter((request) => filterByExpert(getExpertId(request))),
    [pendingRequests, filterByExpert],
  );

  const filteredActiveHourlyRetainers = useMemo(
    () =>
      activeHourlyRetainers.filter((retainer) => {
        const expertMatch = filterByExpert(getExpertId(retainer));
        const workComplete = Boolean(retainer.workCompletedAt);
        const statusMatch =
          retainerStatusFilter === 'all' ||
          (retainerStatusFilter === 'active' && !workComplete) ||
          (retainerStatusFilter === 'completed' && workComplete);
        return expertMatch && statusMatch;
      }),
    [activeHourlyRetainers, filterByExpert, retainerStatusFilter],
  );

  const filteredActiveFlatRetainers = useMemo(
    () =>
      activeFlatRetainers.filter((retainer) => {
        const expertMatch = filterByExpert(getExpertId(retainer));
        const workComplete = Boolean(retainer.workCompletedAt);
        const statusMatch =
          retainerStatusFilter === 'all' ||
          (retainerStatusFilter === 'active' && !workComplete) ||
          (retainerStatusFilter === 'completed' && workComplete);
        return expertMatch && statusMatch;
      }),
    [activeFlatRetainers, filterByExpert, retainerStatusFilter],
  );

  const noRetainerBalance = Boolean(
    balance &&
      balance.hoursPurchased === 0 &&
      (balance.flatAmountPurchased ?? 0) === 0,
  );
  const hasHoursBalance = Boolean(balance && balance.hoursPurchased > 0);
  const hasFlatBalance = Boolean(
    balance && (balance.flatAmountPurchased ?? 0) > 0,
  );

  const pendingApproveLabel = 'Approve & Fund';

  return {
    retainers,
    isLoadingRetainers: isLoading,
    retainersError: error,
    balance,
    activeHourlyRetainers,
    activeFlatRetainers,
    pendingRequests,
    retainerExpertsForFilter,
    filteredPendingRequests,
    filteredActiveFlatRetainers,
    filteredActiveHourlyRetainers,
    noRetainerBalance,
    hasHoursBalance,
    hasFlatBalance,
    pendingApproveLabel,
    historyRetainers,
  };
}
